/**
 * W3C `traceparent` generator for SPA-originated fetches.
 *
 * Format: `00-<32 hex trace_id>-<16 hex span_id>-<flags>`
 * Spec:   https://www.w3.org/TR/trace-context/#traceparent-header
 *
 * Why we need this in the browser:
 *
 *   Lexserver's `sentry-scope` middleware (lexserver/src/middleware/
 *   sentry-scope.ts) calls `extractTraceparentScope(...)` on every
 *   inbound request and threads the resulting trace_id through
 *   lx-obs's `obs.withScope(...)` for the request's lifetime —
 *   meaning every log line (including the downstream lexruntime
 *   forwarder + harn-service hops) shares one trace_id.
 *
 *   Without a `traceparent` header from the SPA, lexserver mints a
 *   fresh trace_id on entry — useful for server-to-server hops but
 *   leaves SPA-originated requests as orphans in the operational-log
 *   (no way to correlate a chat-error in the SPA with the lexserver
 *   route that produced it).
 *
 * Browser SPA adoption of @lexiun/lx-obs proper (`lx-obs/browser`)
 * is a deferred follow-up — for now we keep this small helper local
 * to each SPA. Format strictly matches what lx-obs's
 * parseTraceparent expects so the inbound middleware accepts it.
 *
 * Generation strategy: fresh trace_id per fetch. Each user action
 * (button click → fetch) is treated as one logical operation. A
 * single page-load can produce dozens of independent traces; that's
 * fine and standard for browser instrumentation.
 */

const HEX_TABLE = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, "0"));

function bytesToHex(bytes: Uint8Array): string {
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    out += HEX_TABLE[bytes[i]!];
  }
  return out;
}

/** Returns a fresh `traceparent` header value. Always emits version
 *  `00` and flags `01` (sampled = true; downstream backends respect
 *  the upstream's sampling bit). */
export function newTraceparent(): string {
  const traceBytes = new Uint8Array(16);
  const spanBytes = new Uint8Array(8);
  crypto.getRandomValues(traceBytes);
  crypto.getRandomValues(spanBytes);
  return `00-${bytesToHex(traceBytes)}-${bytesToHex(spanBytes)}-01`;
}
